import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'

import {
  ArtifactCanvasBody,
  ArtifactCanvasFooterBar,
  ArtifactCanvasInlineError,
  ArtifactCanvasSaveActions,
} from '@/components/artifact/artifact-canvas-layout'
import { Button } from '@/components/ui/button'
import { RichTextCharCount } from '@/components/ui/rich-text-char-count'
import { RichTextEditor } from '@/components/ui/rich-text-editor'
import { RichTextField } from '@/components/ui/rich-text-field'
import { MARKDOWN_FIELD_MAX_LENGTH } from '@/constants'
import { richTextVisibleCharCount } from '@/lib/rich-text-visible-char-count'
import type { RichTextChrome } from '@/components/ui/rich-text-shared'

export function useMarkdownArtifactDraft(initialValue: string, entityKey: string) {
  const didHydrateDraftRef = useRef(false)
  const lastServerValueRef = useRef(initialValue)
  const [value, setValue] = useState(() => initialValue)
  const [baselineValue, setBaselineValue] = useState(() => initialValue)
  const [charCount, setCharCount] = useState(() => richTextVisibleCharCount(initialValue))
  const [validationError, setValidationError] = useState<string | null>(null)

  useLayoutEffect(() => {
    didHydrateDraftRef.current = false
    lastServerValueRef.current = initialValue
  }, [entityKey]) // eslint-disable-line react-hooks/exhaustive-deps -- reset only when the edited entity changes

  useLayoutEffect(() => {
    if (!didHydrateDraftRef.current) {
      didHydrateDraftRef.current = true
      lastServerValueRef.current = initialValue
      setValue(initialValue)
      setBaselineValue(initialValue)
      setCharCount(richTextVisibleCharCount(initialValue))
      return
    }
    // Agent / API writes change the stored value — apply them into the open canvas.
    if (initialValue !== lastServerValueRef.current) {
      lastServerValueRef.current = initialValue
      setValue(initialValue)
      setBaselineValue(initialValue)
      setCharCount(richTextVisibleCharCount(initialValue))
    }
  }, [initialValue, entityKey])

  const isDirty = value !== baselineValue

  const handleChange = (next: string) => {
    setValue(next)
    setValidationError(null)
  }

  return {
    value,
    isDirty,
    charCount,
    validationError,
    setValidationError,
    onChange: handleChange,
    onCharCountChange: setCharCount,
  }
}

type RichTextMarkdownArtifactCanvasProps = {
  id: string
  value: string
  onChange: (value: string) => void
  charCount: number
  onCharCountChange: (count: number) => void
  validationError?: string | null
  serverError?: string | null
  onClose: () => void
  onSave: () => void
  isDirty: boolean
  isSaving: boolean
  /** `field` for bio/about; `document` for job descriptions. */
  editorChrome?: RichTextChrome
  saveLabel?: string
  savingLabel?: string
  primaryAction?: ReactNode
  placeholder?: string
}

/** Single markdown field in an artifact canvas — bio, org about, etc. */
export function RichTextMarkdownArtifactCanvas({
  id,
  value,
  onChange,
  charCount,
  onCharCountChange,
  validationError,
  serverError,
  onClose,
  onSave,
  isDirty,
  isSaving,
  editorChrome = 'field',
  saveLabel = 'Save',
  savingLabel = 'Saving…',
  primaryAction,
  placeholder,
}: RichTextMarkdownArtifactCanvasProps) {
  const errorMessage = validationError || serverError
  const editorProps = {
    id,
    frame: 'canvas' as const,
    value,
    onChange,
    maxLength: MARKDOWN_FIELD_MAX_LENGTH,
    onCharCountChange,
    'aria-invalid': charCount > MARKDOWN_FIELD_MAX_LENGTH,
    className: 'min-h-0 flex-1',
    placeholder,
  }

  return (
    <ArtifactCanvasBody className="flex min-h-0 flex-1 flex-col space-y-0 p-0">
      <form
        className="flex min-h-0 flex-1 flex-col"
        onSubmit={(event) => event.preventDefault()}
      >
        {editorChrome === 'document' ? (
          <RichTextEditor {...editorProps} />
        ) : (
          <RichTextField {...editorProps} />
        )}

        <ArtifactCanvasInlineError>{errorMessage}</ArtifactCanvasInlineError>
      </form>

      <ArtifactCanvasFooterBar
        footerClassName="shrink-0"
        leading={
          <RichTextCharCount current={charCount} max={MARKDOWN_FIELD_MAX_LENGTH} />
        }
        actions={
          primaryAction ? (
            <>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              {primaryAction}
            </>
          ) : (
            <ArtifactCanvasSaveActions
              onClose={onClose}
              onSave={onSave}
              isDirty={isDirty}
              isSaving={isSaving}
              saveLabel={saveLabel}
              savingLabel={savingLabel}
            />
          )
        }
      />
    </ArtifactCanvasBody>
  )
}
